/**
 * Pure H OS · Phase D · P0 Interaction Depth · linkedRef helper.
 *
 * Maps a foreign-key `id` (like "d-001", "b-104", "rp-1") + an optional
 * hint about the kind of entity to a Pure H OS route + the search param
 * to open its drawer. Consumed by every module that renders a `linkedTo`
 * string so the ID becomes a real click-through.
 */
export type LinkedKind =
  | "decision" | "batch" | "rnd_product" | "campaign" | "brief"
  | "risk" | "procurement" | "meeting" | "recommendation";

export type LinkedRoute = { path: string; label: string } | null;

/**
 * Best-effort resolver: uses explicit `kind` when provided, otherwise
 * infers from the id prefix. Unknown ids return null (caller should
 * render a plain <code> chip in that case).
 */
export function resolveLinkedRef(id: string | null | undefined, kind?: LinkedKind | string | null): LinkedRoute {
  if (!id) return null;

  // Explicit kind first
  if (kind === "decision" || id.startsWith("d-")) {
    return { path: `/pureh/decisions?open=${encodeURIComponent(id)}`, label: `Decision · ${id}` };
  }
  if (kind === "risk" || id.startsWith("rr-")) {
    return { path: `/pureh/risks?open=${encodeURIComponent(id)}`, label: `Risk · ${id}` };
  }
  if (kind === "brief" || id.startsWith("brf-")) {
    return { path: `/pureh/briefs?open=${encodeURIComponent(id)}`, label: `Brief · ${id}` };
  }
  if (kind === "procurement" || id.startsWith("po-")) {
    return { path: `/pureh/procurement?open=${encodeURIComponent(id)}`, label: `PO · ${id}` };
  }
  if (kind === "meeting" || id.startsWith("mtg-")) {
    return { path: `/pureh/meetings?open=${encodeURIComponent(id)}`, label: `Meeting · ${id}` };
  }
  if (kind === "batch" || id.startsWith("b-")) {
    return { path: `/pureh/operations/batches`, label: `Batch · ${id}` };
  }
  if (kind === "rnd_product" || id.startsWith("rp-")) {
    return { path: `/pureh/rd/library`, label: `Product · ${id}` };
  }
  if (kind === "campaign") {
    return { path: `/pureh/marketing/pipeline`, label: `Campaign · ${id}` };
  }
  if (kind === "recommendation" || id.startsWith("rec-")) {
    return { path: `/pureh/ai/insights`, label: `Rec · ${id}` };
  }
  return null;
}
